import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Divider from '@mui/material/Divider';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Select from '@mui/material/Select';
import FormControl from '@mui/material/FormControl';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import MenuIcon from '@mui/icons-material/Menu';

// Icons
import SmartToyIcon from '@mui/icons-material/SmartToy';
import PersonIcon from '@mui/icons-material/Person';
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver';
import PsychologyIcon from '@mui/icons-material/Psychology';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import DownloadIcon from '@mui/icons-material/Download';
import SettingsIcon from '@mui/icons-material/Settings';
import CloseIcon from '@mui/icons-material/Close';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import NotesIcon from '@mui/icons-material/Notes';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import ChatIcon from '@mui/icons-material/Chat';
import ViewComfyIcon from '@mui/icons-material/ViewComfy';
import ViewCompactIcon from '@mui/icons-material/ViewCompact';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';

import useConversationDensity from './useConversationDensity';

const TopControlBar = ({
  currentMode,
  onModeChange,
  clientName,
  clientId,
  conversationId,
  conversationStatus,
  autoForwardEnabled,
  careNoteCount,
  consultationsCount,
  onConsultSupervisor,
  onToggleAutoplay,
  onExportAudio,
  onOpenSettings,
  onCloseConversation,
  onNavigateToCareNotes,
  isLoading,
  styles = {},
  responsive = {},
  onToggleSidebar
}) => {
  const navigate = useNavigate();
  const { density, setDensity, focusMode, toggleFocus } = useConversationDensity();
  const [audioMenuAnchor, setAudioMenuAnchor] = useState(null);
  const [moreMenuAnchor, setMoreMenuAnchor] = useState(null);
  const [consultationMenuAnchor, setConsultationMenuAnchor] = useState(null);

  const handleModeClick = (mode) => {
    if (mode !== currentMode && !isLoading) {
      onModeChange(mode);
    }
  };

  const handleAudioMenuOpen = (event) => {
    setAudioMenuAnchor(event.currentTarget);
  };

  const handleAudioMenuClose = () => {
    setAudioMenuAnchor(null);
  };

  const handleMoreMenuOpen = (event) => {
    setMoreMenuAnchor(event.currentTarget);
  };

  const handleMoreMenuClose = () => {
    setMoreMenuAnchor(null);
  };

  const handleConsultationMenuOpen = (event) => {
    setConsultationMenuAnchor(event.currentTarget);
  };

  const handleConsultationMenuClose = () => {
    setConsultationMenuAnchor(null);
  };

  const handleViewConsultations = () => {
    handleConsultationMenuClose();
    navigate(`/therapist/dashboard?tab=consultations&clientId=${clientId}`, {
      state: {
        returnTo: 'clientConversation',
        clientId,
        clientName,
        tab: 4, // Conversation tab index
        conversationId // Pass the current conversation ID to restore it
      }
    });
  };

  const handleStartNewConsultation = () => {
    handleConsultationMenuClose();
    if (conversationId) {
      navigate(`/supervisor/consultation/new/${clientId}/${conversationId}`);
    }
  };

  const handleAutoplay = () => {
    handleAudioMenuClose();
    onToggleAutoplay();
  };

  const handleExport = () => {
    handleAudioMenuClose();
    onExportAudio();
  };

  const getModeIcon = (mode) => {
    switch (mode) {
      case 'ai':
        return <SmartToyIcon sx={{ fontSize: 18 }} />;
      case 'human':
        return <PersonIcon sx={{ fontSize: 18 }} />;
      case 'assisted':
        return <RecordVoiceOverIcon sx={{ fontSize: 18 }} />;
      default:
        return null;
    }
  };

  const getModeLabel = (mode) => {
    switch (mode) {
      case 'ai':
        return 'AI';
      case 'human':
        return 'Human';
      case 'assisted':
        return 'Assisted';
      default:
        return mode;
    }
  };

  return (
    <Box
      sx={{
        ...styles.topControlBar,
        // Focus mode: slim the chrome down so the transcript dominates.
        // We keep the bar (and its density switcher) reachable rather than
        // hiding it entirely, since the parent layout shell is owned elsewhere.
        ...(focusMode && !responsive.isMobile
          ? { height: 40, px: 2, boxShadow: 'none' }
          : {})
      }}
    >
      {/* Left Section */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: responsive.isMobile ? 1 : 2 }}>
        {/* Mobile menu button */}
        {responsive.shouldShowDrawerSidebar && (
          <IconButton
            onClick={onToggleSidebar}
            aria-label="Toggle sidebar"
            sx={{
              display: 'flex',
              color: '#64748b'
            }}
          >
            <MenuIcon />
          </IconButton>
        )}

        {/* Mode Selector - Desktop */}
        {!responsive.isMobile && (
          <Box sx={styles.modeSelector}>
            {['ai', 'human', 'assisted'].map((mode) => (
              <Box
                key={mode}
                component="button"
                onClick={() => handleModeClick(mode)}
                disabled={isLoading}
                sx={{
                  ...styles.modeButton,
                  ...(currentMode === mode && styles.modeButtonActive[mode]),
                  opacity: isLoading ? 0.6 : 1,
                  cursor: isLoading ? 'not-allowed' : 'pointer'
                }}
              >
                {getModeIcon(mode)}
                {getModeLabel(mode)}
              </Box>
            ))}
          </Box>
        )}

        {/* Mode Selector - Mobile Dropdown */}
        {responsive.isMobile && (
          <FormControl size="small" sx={{ minWidth: 100 }}>
            <Select
              value={currentMode}
              onChange={(e) => handleModeClick(e.target.value)}
              disabled={isLoading}
              sx={{
                height: 36,
                borderRadius: '8px',
                fontSize: '14px',
                '& .MuiSelect-select': {
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5
                }
              }}
              renderValue={(value) => (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {getModeIcon(value)}
                  <span>{getModeLabel(value)}</span>
                </Box>
              )}
            >
              {['ai', 'human', 'assisted'].map((mode) => (
                <MenuItem key={mode} value={mode}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {getModeIcon(mode)}
                    {getModeLabel(mode)}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </Box>

      {/* Center Section - Status Display */}
      <Box sx={{ 
        ...styles.statusDisplay,
        display: responsive.isMobile ? 'none' : 'flex'
      }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#1e293b' }}>
          {clientName || 'Client'}
        </Typography>
        
        {conversationStatus === 'active' && (
          <Chip
            icon={<FiberManualRecordIcon sx={{ fontSize: 12 }} />}
            label="Active"
            size="small"
            sx={{
              backgroundColor: '#E8F5E9',
              color: '#2E7D32',
              '& .MuiChip-icon': { color: '#2E7D32' }
            }}
          />
        )}

        {currentMode === 'assisted' && autoForwardEnabled && (
          <Chip
            label="Auto-Forward"
            size="small"
            sx={{
              backgroundColor: '#FFF3E0',
              color: '#F57C00',
              fontWeight: 500
            }}
          />
        )}

        {careNoteCount > 0 && (
          <Chip
            icon={<NotesIcon sx={{ fontSize: 16 }} />}
            label={`${careNoteCount} Note${careNoteCount !== 1 ? 's' : ''}`}
            size="small"
            onClick={onNavigateToCareNotes}
            sx={{
              backgroundColor: '#F3E5F5',
              color: '#7B1FA2',
              cursor: 'pointer',
              '& .MuiChip-icon': { color: '#7B1FA2' },
              '&:hover': {
                backgroundColor: '#E1BEE7'
              }
            }}
          />
        )}
      </Box>

      {/* Mobile client name display */}
      {responsive.isMobile && (
        <Typography 
          variant="subtitle2" 
          sx={{ 
            fontWeight: 600, 
            color: '#1e293b',
            flex: 1,
            textAlign: 'center',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}
        >
          {clientName || 'Client'}
        </Typography>
      )}

      {/* Right Section - Action Buttons */}
      <Box sx={styles.actionButtons}>
        {/* Desktop buttons */}
        {!responsive.isMobile && (
          <>
            {/* Density switcher: Comfortable / Compact + Focus toggle */}
            <ToggleButtonGroup
              size="small"
              exclusive
              value={focusMode ? null : density}
              onChange={(e, value) => {
                if (value) {
                  setDensity(value);
                }
              }}
              aria-label="Conversation density"
              sx={{
                mr: 0.5,
                '& .MuiToggleButton-root': {
                  px: 1,
                  py: 0.5,
                  border: '1px solid',
                  borderColor: 'divider'
                }
              }}
            >
              <ToggleButton value="comfortable" aria-label="Comfortable density">
                <Tooltip title="Comfortable">
                  <ViewComfyIcon sx={{ fontSize: 18 }} />
                </Tooltip>
              </ToggleButton>
              <ToggleButton value="compact" aria-label="Compact density">
                <Tooltip title="Compact">
                  <ViewCompactIcon sx={{ fontSize: 18 }} />
                </Tooltip>
              </ToggleButton>
            </ToggleButtonGroup>

            <Tooltip title={focusMode ? 'Exit focus (⌘. / Esc)' : 'Focus mode (⌘.)'}>
              <IconButton
                onClick={toggleFocus}
                aria-label={focusMode ? 'Exit focus mode' : 'Enter focus mode'}
                aria-pressed={focusMode}
                sx={{
                  ...styles.iconButton,
                  color: focusMode ? 'primary.main' : 'text.secondary',
                  backgroundColor: focusMode ? 'action.selected' : 'background.paper'
                }}
              >
                {focusMode ? <FullscreenExitIcon /> : <FullscreenIcon />}
              </IconButton>
            </Tooltip>

            <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

            <Tooltip title="AI Supervisor">
              <IconButton
                onClick={handleConsultationMenuOpen}
                aria-label="AI Supervisor"
                sx={{
                  ...styles.iconButton,
                  color: '#42A5F5',
                  '&:hover': {
                    backgroundColor: '#E3F2FD'
                  }
                }}
              >
                <PsychologyIcon />
              </IconButton>
            </Tooltip>

            <Tooltip title="Audio Options">
              <IconButton
                onClick={handleAudioMenuOpen}
                aria-label="Audio options"
                sx={{
                  ...styles.iconButton,
                  color: '#FF8C00',
                  '&:hover': {
                    backgroundColor: '#FFF3E0'
                  }
                }}
              >
                <VolumeUpIcon />
              </IconButton>
            </Tooltip>

            <Tooltip title="Settings">
              <IconButton
                onClick={onOpenSettings}
                aria-label="Settings"
                sx={{
                  ...styles.iconButton,
                  '&:hover': {
                    backgroundColor: '#F1F5F9'
                  }
                }}
              >
                <SettingsIcon />
              </IconButton>
            </Tooltip>

            <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

            <Tooltip title="Close Conversation">
              <IconButton
                onClick={onCloseConversation}
                aria-label="Close conversation"
                sx={{
                  ...styles.iconButton,
                  color: '#EF5350',
                  '&:hover': {
                    backgroundColor: '#FFEBEE'
                  }
                }}
              >
                <CloseIcon />
              </IconButton>
            </Tooltip>
          </>
        )}

        {/* Mobile more menu */}
        {responsive.isMobile && (
          <IconButton
            onClick={handleMoreMenuOpen}
            aria-label="More options"
            sx={styles.iconButton}
          >
            <MoreVertIcon />
          </IconButton>
        )}
      </Box>

      {/* Audio Menu */}
      <Menu
        anchorEl={audioMenuAnchor}
        open={Boolean(audioMenuAnchor)}
        onClose={handleAudioMenuClose}
        PaperProps={{
          sx: {
            borderRadius: '12px',
            mt: 1,
            minWidth: 180
          }
        }}
      >
        <MenuItem onClick={handleAutoplay}>
          <VolumeUpIcon sx={{ mr: 1, fontSize: 20 }} />
          Autoplay Conversation
        </MenuItem>
        <MenuItem onClick={handleExport}>
          <DownloadIcon sx={{ mr: 1, fontSize: 20 }} />
          Export Audio
        </MenuItem>
      </Menu>

      {/* Mobile More Menu */}
      <Menu
        anchorEl={moreMenuAnchor}
        open={Boolean(moreMenuAnchor)}
        onClose={handleMoreMenuClose}
        PaperProps={{
          sx: {
            borderRadius: '12px',
            mt: 1,
            minWidth: 200
          }
        }}
      >
        {conversationStatus === 'active' && (
          <MenuItem disabled>
            <FiberManualRecordIcon sx={{ mr: 1, fontSize: 16, color: '#2E7D32' }} />
            Active Conversation
          </MenuItem>
        )}
        {careNoteCount > 0 && (
          <>
            <MenuItem onClick={() => { handleMoreMenuClose(); onNavigateToCareNotes(); }}>
              <NotesIcon sx={{ mr: 1, fontSize: 20 }} />
              View {careNoteCount} Care Note{careNoteCount !== 1 ? 's' : ''}
            </MenuItem>
            <Divider />
          </>
        )}
        <MenuItem onClick={(e) => { handleMoreMenuClose(); handleConsultationMenuOpen(e); }}>
          <PsychologyIcon sx={{ mr: 1, fontSize: 20 }} />
          AI Supervisor
        </MenuItem>
        <Divider />
        <MenuItem onClick={(e) => { 
          e.stopPropagation();
          onToggleAutoplay();
          handleMoreMenuClose();
        }}>
          <VolumeUpIcon sx={{ mr: 1, fontSize: 20 }} />
          Autoplay Conversation
        </MenuItem>
        <MenuItem onClick={(e) => { 
          e.stopPropagation();
          onExportAudio();
          handleMoreMenuClose();
        }}>
          <DownloadIcon sx={{ mr: 1, fontSize: 20 }} />
          Export Audio
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => { handleMoreMenuClose(); toggleFocus(); }}>
          {focusMode
            ? <FullscreenExitIcon sx={{ mr: 1, fontSize: 20 }} />
            : <FullscreenIcon sx={{ mr: 1, fontSize: 20 }} />}
          {focusMode ? 'Exit Focus Mode' : 'Focus Mode'}
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => { handleMoreMenuClose(); onOpenSettings(); }}>
          <SettingsIcon sx={{ mr: 1, fontSize: 20 }} />
          Settings
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => { handleMoreMenuClose(); onCloseConversation(); }}>
          <CloseIcon sx={{ mr: 1, fontSize: 20, color: '#EF5350' }} />
          <Typography sx={{ color: '#EF5350' }}>Close Conversation</Typography>
        </MenuItem>
      </Menu>

      {/* AI Supervisor Consultation Menu */}
      <Menu
        anchorEl={consultationMenuAnchor}
        open={Boolean(consultationMenuAnchor)}
        onClose={handleConsultationMenuClose}
        PaperProps={{
          sx: {
            borderRadius: '12px',
            mt: 1,
            minWidth: 250
          }
        }}
      >
        <MenuItem onClick={handleViewConsultations}>
          <ListItemIcon>
            <MenuBookIcon fontSize="small" sx={{ color: '#6366F1' }} />
          </ListItemIcon>
          <ListItemText
            primary="View Consultations"
            secondary={consultationsCount > 0 ? `${consultationsCount} consultation${consultationsCount > 1 ? 's' : ''}` : 'None yet'}
          />
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleStartNewConsultation}>
          <ListItemIcon>
            <ChatIcon fontSize="small" sx={{ color: '#6366F1' }} />
          </ListItemIcon>
          <ListItemText
            primary="Start New Consultation"
            secondary="Get clinical guidance"
          />
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default TopControlBar;